import {
  createElement,
  KeyboardEventHandler,
  MouseEventHandler,
  FocusEventHandler,
  forwardRef,
  AriaAttributes,
  useState,
  useRef,
} from 'react';
import { Node } from './';
import { StatusIconMap } from './__internal__/StatusIconsList';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { postfixTestId, TestIdProps } from '../../util/testId';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { BrowserName, getBrowserName } from '../../util';
import { ContextMenu } from '../ContextMenu';
import { TreeExpandClosed, TreeExpandOpened } from '@sas/react-svg';
import { CustomTooltip } from '../CustomTooltip';
import { useKeyboardTooltip } from '../Settings';
import { setRef } from '../../util/ref-util';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-Tree-gui-icu.properties';
import { useIconA11yInfo } from '../Icon/useIconA11yInfo';
import { SVGIconDescriptor } from '../SVG';
import { InvisibleText } from '../InvisibleText';

// Import CSS last
import cx from 'classnames';
import cn from './TreeNode.css.json';
import './TreeNode.css';

export interface TreeNodeProps extends TestIdProps {
  node: Node;
  onArrowClick?: (id: string) => void;
  onDoubleClick?: (node: Node) => void;
  onSelect?: (
    node: Node,
    eventData?: { keyPressed?: string; ctrlKey: boolean; shiftKey: boolean; metaKey: boolean }
  ) => void;
  selected: boolean;
  expanded: boolean;
  tabIndex?: number;
  isChildNode?: boolean;
  hasChildren?: boolean;
  onContext?: (node: Node) => void;
  onCustomTooltip?: (node: Node) => void;
  onKeyDown?: KeyboardEventHandler;
  onMouseDown?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  depth?: AriaAttributes['aria-level'];
  siblingCount?: AriaAttributes['aria-setsize'];
  siblingPosition?: AriaAttributes['aria-posinset'];
  disabled?: boolean;
}

const useTextLocalizer = createUseTextLocalizer(bundle);

export const TreeNode = forwardRef<HTMLDivElement, TreeNodeProps>((props, ref) => {
  const localize = useTextLocalizer();
  function onLabelClick(e: React.MouseEvent<HTMLElement>) {
    if (!!props.node.disabled) return;
    if (props.onSelect)
      props.onSelect(props.node, {
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
      });
  }
  function onArrowClick(e: React.MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    if (props.onArrowClick) props.onArrowClick(props.node.id);
  }

  const dt = useDesignTokens();
  const nodeLabelStyle = renderInlineCssVars({
    'label-fg': props.node.disabled
      ? 'var(--tree-disabled-fg)'
      : props.selected
      ? 'var(--tree-selected-fg)'
      : 'var(--tree-fg)',
    'label-text-cursor': 'default',
  });
  const getIconA11yInfo = useIconA11yInfo();

  const iconTooltip = props.node.icon
    ? getIconA11yInfo({
        ...('icon' in props.node.icon ? { ...props.node.icon } : { icon: props.node.icon }),
      }).tooltip
    : undefined;

  const statusIconTooltip = props.node.statusIcon
    ? props.node.statusIconTooltip ??
      getIconA11yInfo({ icon: StatusIconMap[props.node.statusIcon] as SVGIconDescriptor }).tooltip
    : undefined;

  const tokens = renderInlineCssVars(
    {
      'selected-focus-bg':
        dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)',
    },
    'tree-node'
  );

  const hasChildren =
    props.hasChildren || (Array.isArray(props.node.children) && props.node.children.length > 0);
  const [isCustomTooltipOpen, setIsCustomTooltipOpen] = useState(false);

  const getNodeTooltip = () => {
    let tooltip = '';
    const nodeLabel = props.node.required
      ? `${props.node.text}, ${localize('tree.requiredNodeLabel.label')}`
      : props.node.text;

    if (iconTooltip) {
      tooltip = `${iconTooltip}, ${nodeLabel}`;
    } else {
      tooltip = nodeLabel;
    }
    if (statusIconTooltip) {
      tooltip += `, ${statusIconTooltip}`;
    }
    if (!iconTooltip && !statusIconTooltip) {
      tooltip = '';
    }
    if (props.isChildNode && props.node.customTooltip) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${localize('view.keyboard.tooltip.label')}`;
      } else {
        tooltip = localize('view.keyboard.tooltip.label');
      }
    }
    return tooltip;
  };

  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: getNodeTooltip(),
  });

  const focusRef = useRef<HTMLDivElement>(null);
  const [disableFocusLock, setDisableFocusLock] = useState(true);
  return (
    <ContextMenu
      onContextMenu={() => {
        const onContext = props.onContext;
        if (onContext) onContext(props.node);
      }}
    >
      <CustomTooltip
        {...props.node.customTooltip}
        focusLockProps={{ disabled: disableFocusLock }}
        testId={postfixTestId(props.testId, 'customTooltip')}
        isOpen={isCustomTooltipOpen}
        onOpen={() => {
          setIsCustomTooltipOpen(true);
        }}
        onClose={() => {
          setIsCustomTooltipOpen(false);
          setDisableFocusLock(true);
        }}
        hideArrow={false}
      >
        <div
          className={cx(cn.item, props.selected && cn.selected)}
          style={tokens}
          role="treeitem"
          {...(hasChildren && { 'aria-expanded': props.expanded })}
          {...(props?.depth && { 'aria-level': props.depth })}
          {...(props?.siblingCount && { 'aria-setsize': props.siblingCount })}
          {...(props?.siblingPosition && { 'aria-posinset': props.siblingPosition })}
          data-testid={props.testId}
          onClick={onLabelClick}
          aria-selected={props.selected}
          aria-disabled={!!props.node.disabled}
          onKeyDown={(e) => {
            props.onKeyDown?.(e);
            if (e.shiftKey && e.key === 'F10') {
              if (props.isChildNode && props.node.customTooltip?.content) {
                if (getBrowserName() === BrowserName.Firefox) {
                  setTimeout(() => {
                    setIsCustomTooltipOpen(true);
                    setDisableFocusLock(false);
                  }, 10);
                } else {
                  setIsCustomTooltipOpen(true);
                  setDisableFocusLock(false);
                }
                e.preventDefault();
                e.stopPropagation();
              }
            }
          }}
          onFocus={props.onFocus}
          tabIndex={props.tabIndex}
          onDoubleClick={() => props.onDoubleClick?.(props.node)}
          ref={(el) => {
            setKeyboardTooltipRef(el);
            setRef(ref, el);
            setRef(focusRef, el);
          }}
        >
          <div
            className={cn.itemInner}
            style={{
              ...(props?.depth && {
                // Subtract 1 from the depth if it has children to allow room for the expand/collapse icon.
                // Add 0.125 to the depth if it has no children. This provides 1.125rem (the width of the expand icon + margin)
                // difference between siblings with and without children so sibling labels align vertically.
                paddingInlineStart: `${hasChildren ? props.depth - 1 : props.depth + 0.125}rem`,
              }),
            }}
          >
            {hasChildren ? (
              <Icon
                onClick={onArrowClick}
                className={cn.expandIcon}
                icon={props.expanded ? TreeExpandOpened : TreeExpandClosed}
                testId="expandIcon"
                tokens={() => {
                  return {
                    fg: props.selected ? 'var(--tree-selected-fg)' : 'var(--tree-fg)',
                  };
                }}
              />
            ) : null}
            {props.node.icon ? (
              <Icon
                className={cn.nodeIcon}
                disabled={!!props.node.disabled}
                {...('icon' in props.node.icon
                  ? { ...props.node.icon }
                  : { icon: props.node.icon })}
                {...(props.selected && dt.themeVariant === 'high-contrast'
                  ? {
                      tokens: () => ({
                        fg: 'var(--tree-selected-fg)',
                      }),
                    }
                  : {})}
              />
            ) : null}
            <Label
              required={!!props.node.required}
              wrapText={false}
              className={cn.nodeLabel}
              style={nodeLabelStyle}
              disabled={!!props.node.disabled}
            >
              {props.node.text}
            </Label>
            {props.node.required && (
              <InvisibleText srOnly>{localize('tree.requiredNodeLabel.label')}</InvisibleText>
            )}
            {props.node.statusIcon && (
              <Icon
                className={cn.nodeStatusIcon}
                disabled={!!props.node.disabled}
                icon={StatusIconMap[props.node.statusIcon]}
                ariaLabel={props.node.statusIconAriaLabel}
                tooltip={statusIconTooltip}
              />
            )}
          </div>
        </div>
      </CustomTooltip>
    </ContextMenu>
  );
});
